import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectCurrentUser } from '../../auth/store/auth.selectors';
import { OrgSwitchReloadService } from '../../navigation/org-switch-reload';
import { AuthProfileService } from '../auth-profile.service';
import { OrgContextService } from '../org-context.service';
import { toUserContext } from '../user-context.mapper';
import { UserContextActions } from './user-context.actions';

/**
 * In-app organization switching (FDD §4.2, side-nav switcher). Validates the org
 * via POST /auth/switch-org, makes it the active org (so X-Organization-Id on every
 * subsequent request points at it — see orgHeaderInterceptor), refreshes the shell's
 * UserContext, and emits `orgChanged`, which reloads the open page for the new org.
 * A failed switch is toasted by errorInterceptor and leaves the current org intact.
 */
@Injectable()
export class UserContextEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly authProfile = inject(AuthProfileService);
  private readonly orgContext = inject(OrgContextService);
  private readonly orgSwitchReload = inject(OrgSwitchReloadService);

  readonly switchOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserContextActions.switchOrg),
      withLatestFrom(this.store.select(selectCurrentUser)),
      switchMap(([{ orgId }, user]) =>
        this.authProfile.switchOrg(orgId).pipe(
          mergeMap((org) => {
            this.orgContext.setActiveOrg(org);
            const orgChanged = UserContextActions.orgChanged({ orgId: org.orgId });
            const profile = this.orgContext.profile();
            return profile
              ? [
                  UserContextActions.loadContextSuccess({
                    context: toUserContext(profile, org, user.displayName ?? ''),
                  }),
                  orgChanged,
                ]
              : [orgChanged];
          }),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  /**
   * Reload the open page for the new org: it is re-created, so it re-runs its
   * own initial API calls, and its guards re-check the new org's role, data types and
   * Location Management. A page tied to the previous org's record goes back to its list.
   */
  readonly reloadActivePage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserContextActions.orgChanged),
        tap(() => void this.orgSwitchReload.reloadActivePage()),
      ),
    { dispatch: false },
  );
}
